// Main-thread wrapper around the wind tunnel's batch worker (src/sim/batchWorker.ts). One call in, one
// array of per-seed results out, with progress along the way.
//
// There is exactly one batch at a time: starting a second cancels the first. Two batches would be two
// workers competing for cores on a machine that is also drawing a map, and the panel only has room for
// one answer anyway.

import type { BatchPlanOrder, BatchResult } from './batchRun';
import type { FromBatchWorker, ToBatchWorker } from './batchWorker';
import type { AoMeta, Dials, Scenario } from './types';

export type { BatchResult, BatchPlanOrder };

export interface BatchArgs {
  meta: AoMeta;
  buffers: { cost: ArrayBuffer; cover: ArrayBuffer; elev: ArrayBuffer; roads: ArrayBuffer };
  scenario: Scenario;
  dials: Dials;
  plan: BatchPlanOrder[];
  seeds: number[];
  maxTicks: number;
}

export type ProgressHandler = (done: number, total: number) => void;

/** The one batch in flight, if any. */
let current: Worker | null = null;

/**
 * Runs `plan` once per seed and resolves with the results in seed order.
 *
 * Rejects with `batch cancelled` if `cancel()` (or another `runBatch`) interrupts it — the caller is
 * expected to treat that as "no answer", not as a failure worth showing.
 */
export function runBatch(args: BatchArgs, onProgress?: ProgressHandler): Promise<BatchResult[]> {
  cancel();
  const worker = new Worker(new URL('./batchWorker.ts', import.meta.url), { type: 'module' });
  current = worker;

  return new Promise<BatchResult[]>((resolve, reject) => {
    const results: BatchResult[] = [];
    const finish = (fn: () => void): void => {
      worker.terminate();
      if (current === worker) current = null;
      fn();
    };

    worker.onmessage = (e: MessageEvent<FromBatchWorker>): void => {
      const msg = e.data;
      switch (msg.type) {
        case 'result': {
          const { type: _type, ...result } = msg;
          results.push(result);
          break;
        }
        case 'progress':
          onProgress?.(msg.done, msg.total);
          break;
        case 'done':
          finish(() => resolve(results));
          break;
        case 'error':
          finish(() => reject(new Error(msg.message)));
          break;
      }
    };
    worker.onerror = (e): void => finish(() => reject(new Error(e.message || 'batch worker failed')));

    // The buffers are structured-cloned rather than transferred: the store keeps its own copies for the
    // live worker, and a transferred ArrayBuffer would be detached out from under it.
    const msg: ToBatchWorker = { type: 'run', ...args };
    worker.postMessage(msg);

    // A cancellation has to reject this promise, and only the worker instance identifies which batch it
    // belongs to, so the rejector is parked on the worker itself.
    (worker as Worker & { __wtCancel?: () => void }).__wtCancel = () =>
      finish(() => reject(new Error('batch cancelled')));
  });
}

/** Stops the batch in flight, if there is one. Safe to call when there is not. */
export function cancel(): void {
  const worker = current as (Worker & { __wtCancel?: () => void }) | null;
  current = null;
  worker?.__wtCancel?.();
}
